export const validURL = (str) => {
  const pattern = new RegExp('^(https?:\\/\\/)'+ // protocol
    '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|'+ // domain name
    '((\\d{1,3}\\.){3}\\d{1,3}))'+ // OR ip (v4) address
    '(\\:\\d+)?(\\/[-a-z\\d%_.~+@]*)*'+ // port and path
    '(\\?[;&a-z\\d%_.~+=-]*)?'+ // query string
    '(\\#[-a-z\\d_]*)?$','i'); // fragment locator

  return pattern.test(str);
}

export const isImageUrl = (url) => {
  try {
    const parsedUrl = new URL(url);

    return /\.(jpg|jpeg|png|webp|avif|gif|svg)$/i.test(parsedUrl.pathname);
  } catch {
    return false; // Invalid URL
  }
}

export const openNewUrlTab = (url) => {
  window.open(url, '_blank');
};

export const ensureHttps = (url) => {
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return 'https://' + url;
  }

  return url;
}

const decodeAmp = (url) => url.replace(/&amp;/gi, '&');

// Infra / embed-plumbing URLs that are never the content the user meant to paste.
const isJunkUrl = (url) => (
  /\.(js|css)(\?|#|$)/i.test(url)
  || /platform\.twitter\.com|syndication\.twitter\.com|widgets\.js|embed\.js|connect\.facebook\.net|w3\.org|schema\.org/i.test(url)
);

// Canonical social-post URL shapes, in preference order.
const POST_PATTERNS = [
  /\/status(?:es)?\/\d+/i, // twitter / x
  /instagram\.com\/(?:p|reel|tv)\//i, // instagram
  /tiktok\.com\/@[^/]+\/video\/\d+/i, // tiktok
  /youtube\.com\/(?:watch\?|shorts\/|embed\/)|youtu\.be\//i, // youtube
  /facebook\.com\/[^"'<>\s]+\/(?:posts|videos|photos|permalink)\/|fb\.watch\/|story_fbid=/i, // facebook
  /bsky\.app\/profile\/[^/]+\/post\//i, // bluesky
];

// Normalize a YouTube embed / short / shorts URL to the canonical watch URL.
const normalizeYouTube = (url) => {
  const match = url.match(/(?:youtube\.com\/(?:embed|shorts)\/|youtu\.be\/)([A-Za-z0-9_-]{6,})/i);

  return match ? `https://www.youtube.com/watch?v=${match[1]}` : url;
};

// Pull every candidate URL out of an HTML block, most-canonical source first:
// provider attributes (cite / data-href / data-*-permalink) → href / src → raw tokens.
const collectUrls = (html) => {
  const urls = [];
  const attrRe = /(?:cite|data-href|data-instgrm-permalink|data-url)\s*=\s*["']([^"']+)["']/gi;
  const linkRe = /(?:href|src)\s*=\s*["']([^"']+)["']/gi;
  const rawRe = /https?:\/\/[^\s"'<>]+/gi;
  let match;

  while ((match = attrRe.exec(html))) urls.push(match[1]);
  while ((match = linkRe.exec(html))) urls.push(match[1]);
  while ((match = rawRe.exec(html))) urls.push(match[0]);

  return urls.map((url) => decodeAmp(url.trim())).filter((url) => /^https?:\/\//i.test(url));
};

// Smart-detect a clean URL when a whole HTML block is pasted into a URL field.
// Conservative: input that doesn't look like markup passes through untouched.
export const extractUrlFromInput = (input) => {
  if (typeof input !== 'string') {
    return input;
  }

  const looksLikeHtml = /<[a-z!/]/i.test(input) || /\b(?:href|src|cite|data-href)\s*=/i.test(input);

  if (!looksLikeHtml) {
    return input;
  }

  const candidates = collectUrls(input);

  if (!candidates.length) {
    return input;
  }

  const clean = candidates.filter((url) => !isJunkUrl(url));
  const pool = clean.length ? clean : candidates;
  const canonical = pool.find((url) => POST_PATTERNS.some((pattern) => pattern.test(url)));

  return normalizeYouTube(canonical || pool[0]);
}
